// Reserve the largest already-mounted tab or step before the dialog is painted.
// Owners of lazy or asynchronously loaded views must provide their own size budget.
const controllers = new WeakMap();
const HEIGHT = '--bayhive-dialog-height';
const WIDTH = '--bayhive-dialog-width';
const SIZED = 'data-bayhive-dialog-sized';
const PANEL_GROUPS = '.p-tabpanels, .p-steppanels, .tab-content, [data-bayhive-dialog-panels]';
const PANELS = '.p-tabpanel, .p-steppanel, .tab-pane, [data-bayhive-dialog-panel]';
const LOADING =
  '[aria-busy="true"], .p-skeleton, .fa-spin, .p-icon-spin, .spinner-border, .spinner-grow, .p-progressspinner, .p-datatable-loading-icon, .p-treetable-loading-icon';
const OMITTED_ELEMENTS = new Set(['script', 'style', 'link', 'source', 'track', 'base']);
const RESOURCE_ELEMENTS = new Set(['iframe', 'img', 'image', 'video', 'audio', 'object', 'embed']);
const RESOURCE_ATTRIBUTES = new Set([
  'src',
  'srcset',
  'poster',
  'data',
  'href',
  'xlink:href',
  'autofocus',
  'id',
  'is',
  'name',
  'form',
]);

const copyAttributes = (source, target) => {
  for (const attribute of source.attributes) {
    if (
      RESOURCE_ATTRIBUTES.has(attribute.name) ||
      attribute.name.startsWith('on') ||
      attribute.name === 'style'
    )
      continue;
    target.setAttribute(attribute.name, attribute.value);
  }
  for (const property of source.style || []) {
    const value = source.style.getPropertyValue(property);
    if (!value.includes('url(')) target.style.setProperty(property, value);
  }
  target.style.backgroundImage = 'none';
  target.style.maskImage = 'none';
  target.style.animation = 'none';
  target.style.transition = 'none';
};

const resourcePlaceholder = (source) => {
  const placeholder = document.createElement('div');
  copyAttributes(source, placeholder);
  const style = window.getComputedStyle(source);
  placeholder.style.display = style.display === 'inline' ? 'inline-block' : style.display;
  placeholder.style.boxSizing = style.boxSizing;
  placeholder.style.width = source.offsetWidth ? `${source.offsetWidth}px` : style.width;
  placeholder.style.height = source.offsetHeight ? `${source.offsetHeight}px` : style.height;
  placeholder.style.maxWidth = style.maxWidth;
  placeholder.style.maxHeight = style.maxHeight;
  placeholder.style.aspectRatio = style.aspectRatio;
  if (source.naturalWidth && source.naturalHeight && style.aspectRatio === 'auto') {
    placeholder.style.aspectRatio = `${source.naturalWidth} / ${source.naturalHeight}`;
  }
  return placeholder;
};

// Recreate passive DOM only: cloning an iframe/custom element can start its own
// document or lifecycle when connected, even when its containing tree is inert.
const measurementCopy = (source) => {
  if (source.nodeType === Node.TEXT_NODE) return document.createTextNode(source.textContent);
  if (source.nodeType !== Node.ELEMENT_NODE) return null;
  const name = source.localName;
  if (OMITTED_ELEMENTS.has(name)) return null;
  if (
    RESOURCE_ELEMENTS.has(name) ||
    name.includes('-') ||
    (name === 'input' && source.type === 'image')
  ) {
    return resourcePlaceholder(source);
  }
  const copy = document.createElementNS(source.namespaceURI, source.localName);
  copyAttributes(source, copy);
  for (const child of source.childNodes) {
    const childCopy = measurementCopy(child);
    if (childCopy) copy.append(childCopy);
  }
  return copy;
};

const reserveMountedPanels = (copy) => {
  for (const group of copy.querySelectorAll(PANEL_GROUPS)) {
    const panels = [...group.children].filter((child) => child.matches(PANELS));
    if (panels.length < 2) continue;
    group.style.display = 'grid';
    group.style.gridTemplateColumns = 'minmax(0, 1fr)';
    group.style.alignItems = 'start';
    for (const panel of panels) {
      panel.style.removeProperty('display');
      if (panel.classList.contains('tab-pane')) panel.style.display = 'block';
      panel.removeAttribute('hidden');
      panel.style.gridArea = '1 / 1';
      panel.style.alignSelf = 'start';
    }
  }
};

const naturalHeight = (dialog, width) => {
  const copy = measurementCopy(dialog);
  const computed = window.getComputedStyle(dialog);
  for (const property of computed) {
    if (property.startsWith('--'))
      copy.style.setProperty(property, computed.getPropertyValue(property));
  }
  for (const property of [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'lineHeight',
    'letterSpacing',
    'direction',
  ]) {
    copy.style[property] = computed[property];
  }
  copy.inert = true;
  copy.removeAttribute(SIZED);
  copy.style.removeProperty(HEIGHT);
  copy.style.removeProperty(WIDTH);
  copy.setAttribute('data-bayhive-dialog-measuring', '');
  copy.setAttribute('aria-hidden', 'true');
  copy.removeAttribute('aria-modal');
  copy.removeAttribute('role');
  Object.assign(copy.style, {
    position: 'fixed',
    inset: '0 auto auto -100000px',
    visibility: 'hidden',
    pointerEvents: 'none',
    transform: 'none',
    width: `${width}px`,
    minWidth: '0',
    maxWidth: 'none',
    height: 'auto',
    minHeight: '0',
    maxHeight: 'none',
    overflow: 'visible',
  });
  for (const content of copy.children) {
    if (!content.matches('.p-dialog-content, .modal-body')) continue;
    Object.assign(content.style, {
      flex: 'none',
      height: 'auto',
      minHeight: '0',
      maxHeight: 'none',
      overflow: 'auto',
    });
  }
  reserveMountedPanels(copy);
  try {
    (dialog.parentElement || document.body).append(copy);
    return copy.offsetHeight;
  } finally {
    copy.remove();
  }
};

const explicitlySized = (dialog) =>
  dialog.classList.contains('bayhive-dialog--custom') ||
  Boolean(dialog.style.height && dialog.style.height !== 'auto') ||
  Boolean(dialog.style.blockSize && dialog.style.blockSize !== 'auto');

const isVisible = (element) =>
  element.getClientRects().length > 0 &&
  !element.closest('[hidden], [aria-hidden="true"], [inert]') &&
  !['hidden', 'collapse'].includes(window.getComputedStyle(element).visibility);

const visibleLoading = (dialog) => {
  const indicators = [...dialog.querySelectorAll(LOADING)];
  if (dialog.matches(LOADING)) indicators.unshift(dialog);
  return indicators.some(isVisible);
};

const hasReadyMountedPanel = (dialog) =>
  [...dialog.querySelectorAll(PANEL_GROUPS)].some((group) => {
    const panels = [...group.children].filter((panel) => panel.matches(PANELS));
    return (
      panels.length > 1 &&
      panels.some(
        (panel) =>
          !isVisible(panel) &&
          !panel.matches(LOADING) &&
          !panel.querySelector(LOADING) &&
          (panel.textContent.trim() ||
            panel.querySelector('input:not([type="hidden"]), textarea, select, table')),
      )
    );
  });

const createController = (dialog, { lockWidth = true } = {}) => {
  let measuredHeight = '';
  let measuredWidth = '';
  let viewportWidth = window.innerWidth;
  let frame = null;
  let listening = false;
  let initialMeasurementComplete = false;
  let loadingObserver = null;
  const ownsWidth =
    lockWidth &&
    !dialog.style.width &&
    !dialog.style.inlineSize &&
    !dialog.style.getPropertyValue(WIDTH);

  const measure = () => {
    frame = null;
    if (
      !dialog.isConnected ||
      explicitlySized(dialog) ||
      dialog.classList.contains('p-dialog-maximized')
    )
      return;
    if (
      dialog.style.getPropertyValue(HEIGHT) &&
      dialog.style.getPropertyValue(HEIGHT) !== measuredHeight
    )
      return;
    const previousHeight = dialog.style.getPropertyValue(HEIGHT);
    const previousWidth = dialog.style.getPropertyValue(WIDTH);
    const previouslySized = dialog.hasAttribute(SIZED);
    const lockNaturalWidth =
      ownsWidth &&
      !dialog.style.width &&
      !dialog.style.inlineSize &&
      (!previousWidth || previousWidth === measuredWidth);
    const restoreFrame = () => {
      if (previousHeight) dialog.style.setProperty(HEIGHT, previousHeight);
      else dialog.style.removeProperty(HEIGHT);
      if (previousWidth) dialog.style.setProperty(WIDTH, previousWidth);
      else dialog.style.removeProperty(WIDTH);
      if (previouslySized) dialog.setAttribute(SIZED, '');
    };
    dialog.removeAttribute(SIZED);
    if (measuredWidth && previousWidth === measuredWidth) dialog.style.removeProperty(WIDTH);
    const width = dialog.offsetWidth;
    if (!width) {
      restoreFrame();
      return false;
    }
    try {
      const height = Math.max(naturalHeight(dialog, width), dialog.offsetHeight);
      if (height > 0) {
        measuredHeight = `${height}px`;
        dialog.style.setProperty(HEIGHT, measuredHeight);
        if (lockNaturalWidth) {
          measuredWidth = `${width}px`;
          dialog.style.setProperty(WIDTH, measuredWidth);
        }
        dialog.setAttribute(SIZED, '');
        return true;
      }
    } catch {
      // Keep the preceding frame if a replaced subtree cannot be measured.
    }
    restoreFrame();
    return false;
  };
  const disconnectLoadingObserver = () => {
    loadingObserver?.disconnect();
    loadingObserver = null;
  };
  const scheduleInitialMeasurement = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      measureInitialContent();
    });
  };
  const measureInitialContent = () => {
    if (!dialog.isConnected || initialMeasurementComplete) return;
    if (
      explicitlySized(dialog) ||
      (dialog.style.getPropertyValue(HEIGHT) &&
        dialog.style.getPropertyValue(HEIGHT) !== measuredHeight)
    ) {
      disconnectLoadingObserver();
      initialMeasurementComplete = true;
      return;
    }
    if (visibleLoading(dialog) && !hasReadyMountedPanel(dialog)) {
      if (!loadingObserver) {
        loadingObserver = new MutationObserver(scheduleInitialMeasurement);
        loadingObserver.observe(dialog, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-busy'],
        });
      }
      return;
    }
    // Loading may change the first frame once; subsequent tab/state changes
    // only scroll inside the measured frame. Never retain a content observer.
    disconnectLoadingObserver();
    initialMeasurementComplete = Boolean(measure());
  };
  const resize = () => {
    if (window.innerWidth === viewportWidth) return;
    viewportWidth = window.innerWidth;
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      frame = null;
      if (initialMeasurementComplete) measure();
      else measureInitialContent();
    });
  };
  const stop = () => {
    window.removeEventListener('resize', resize);
    disconnectLoadingObserver();
    listening = false;
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = null;
  };
  return {
    start() {
      const viewportChanged = viewportWidth !== window.innerWidth;
      viewportWidth = window.innerWidth;
      if (!initialMeasurementComplete) measureInitialContent();
      else if (viewportChanged) measure();
      if (!listening) window.addEventListener('resize', resize);
      listening = true;
    },
    stop,
    destroy() {
      stop();
      if (measuredHeight) dialog.removeAttribute(SIZED);
      if (measuredHeight && dialog.style.getPropertyValue(HEIGHT) === measuredHeight)
        dialog.style.removeProperty(HEIGHT);
      if (measuredWidth && dialog.style.getPropertyValue(WIDTH) === measuredWidth)
        dialog.style.removeProperty(WIDTH);
    },
  };
};

export const startDialogStableSize = (dialog, options) => {
  if (!dialog?.isConnected || explicitlySized(dialog)) return;
  if (!controllers.has(dialog)) {
    if (dialog.style.getPropertyValue(HEIGHT)) return;
    controllers.set(dialog, createController(dialog, options));
  }
  controllers.get(dialog).start();
};

export const stopDialogStableSize = (dialog) => controllers.get(dialog)?.stop();

export const destroyDialogStableSize = (dialog) => {
  controllers.get(dialog)?.destroy();
  controllers.delete(dialog);
};

export const bayhiveDialogStableSize = {
  root: {
    onVnodeUnmounted(vnode) {
      destroyDialogStableSize(vnode.el);
    },
  },
  transition: {
    onEnter(dialog) {
      if (dialog.classList.contains('bayhive-dialog')) startDialogStableSize(dialog);
    },
    onBeforeLeave(dialog) {
      // Keep the measured frame during the closing animation.
      stopDialogStableSize(dialog);
    },
  },
};
